import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  UsersIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export const ManualPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'intro' | 'owner' | 'delegation' | 'admin'>('intro');
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased selection:bg-blue-600 selection:text-white">
      {/* Navbar */}
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors font-medium text-sm">
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Volver a la página principal</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-bold text-blue-700 ring-1 ring-inset ring-blue-700/10">
              v1.0 Oficial
            </span>
            <span className="font-bold text-slate-900">Manual de Uso Oficial</span>
          </div>
        </div>
      </header>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation Sidebar */}
          <div className="lg:col-span-1 space-y-2">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sticky top-24 space-y-1.5">
              <p className="px-3 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Contenido</p>
              <button
                onClick={() => setActiveSection('intro')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-semibold transition-all ${
                  activeSection === 'intro'
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20 font-bold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <BookOpenIcon className="w-4 h-4 shrink-0" />
                <span>1. Introducción y Acceso</span>
              </button>

              <button
                onClick={() => setActiveSection('owner')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-semibold transition-all ${
                  activeSection === 'owner'
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20 font-bold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <UsersIcon className="w-4 h-4 shrink-0" />
                <span>2. Guía del Propietario</span>
              </button>

              <button
                onClick={() => setActiveSection('delegation')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-semibold transition-all ${
                  activeSection === 'delegation'
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20 font-bold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <ShieldCheckIcon className="w-4 h-4 shrink-0" />
                <span>3. Poderes y Delegación</span>
              </button>

              <button
                onClick={() => setActiveSection('admin')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-semibold transition-all ${
                  activeSection === 'admin'
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20 font-bold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <ChartBarIcon className="w-4 h-4 shrink-0" />
                <span>4. Panel de Administración</span>
              </button>
            </div>
          </div>

          {/* Section Detail Panel */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-8 lg:p-12 shadow-sm max-w-none">
              {/* ─── CAPÍTULO 1 ────────────────────────────────────────────── */}
              {activeSection === 'intro' && (
                <div className="space-y-8">
                  <div className="border-b border-slate-100 pb-6">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Capítulo 1</span>
                    <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 mt-1 tracking-tight">
                      Introducción y Acceso Seguro
                    </h1>
                    <p className="text-sm text-slate-500 mt-2">
                      Conoce la arquitectura general del sistema y los métodos de autenticación autorizados.
                    </p>
                  </div>

                  <div className="text-slate-600 leading-relaxed space-y-4">
                    <p>
                      <strong>DH Assembly</strong> es una solución de nivel empresarial diseñada a medida para comunidades bajo el régimen de propiedad horizontal en Colombia. Su propósito central es garantizar la validez jurídica, el orden y la absoluta transparencia en la toma de decisiones comunitarias durante asambleas ordinarias y extraordinarias.
                    </p>
                    <p>
                      La plataforma opera bajo el marco normativo de la <strong>Ley 675 de 2001</strong>, calculando en tiempo real el quórum y los resultados de votación basándose estrictamente en los <strong>coeficientes de copropiedad</strong> registrados.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-slate-900">Flujo de Creación de Usuarios y Acceso</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      A diferencia de plataformas abiertas, en <strong>DH Assembly</strong> no existe un formulario de auto-registro público. Para proteger la integridad del padrón electoral de la comunidad, los copropietarios son creados exclusivamente a través del proceso de <strong>importación masiva</strong> gestionado por la administración del conjunto residencial.
                    </p>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 space-y-4">
                      <h4 className="text-sm font-bold text-slate-900">Paso a paso para iniciar sesión:</h4>
                      <ol className="list-decimal list-inside space-y-2.5 text-sm text-slate-600 font-medium">
                        <li>
                          Ingresa a la URL oficial de la plataforma y dirígete al módulo de{' '}
                          <Link to="/login" className="text-blue-600 underline font-bold hover:text-blue-800">
                            Iniciar Sesión
                          </Link>.
                        </li>
                        <li>
                          Digita tu correo electrónico registrado en la administración o tu número de documento de identidad.
                        </li>
                        <li>
                          Ingresa la contraseña temporal enviada a tu correo de bienvenida o tu contraseña personal configurada.
                        </li>
                        <li>
                          <strong>Autenticación en dos pasos (2FA):</strong> Si la administración lo ha requerido para tu conjunto, el sistema enviará un código de verificación seguro a tu correo electrónico para validar la sesión.
                        </li>
                      </ol>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-slate-900">Manejo de Multi-Propiedad</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Si eres propietario de múltiples inmuebles dentro del mismo conjunto residencial (por ejemplo, un apartamento, un parqueadero privado y un depósito con matrículas independientes), el administrador consolidará la sumatoria total de tus <strong>coeficientes de copropiedad</strong> bajo tu cuenta principal. Al emitir tu voto, este aplicará automáticamente con el peso unificado de todas tus propiedades.
                    </p>
                  </div>

                  <div className="rounded-xl bg-blue-50/80 border border-blue-100 p-4 text-sm text-blue-900 flex items-start gap-3">
                    <ExclamationTriangleIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold">Seguridad de Sesión Única:</strong> El sistema cuenta con un mecanismo avanzado que previene inicios de sesión concurrentes con las mismas credenciales. Si inicias sesión en un nuevo dispositivo, la sesión anterior se cerrará automáticamente para garantizar que el voto sea emitido por una única persona autorizada en tiempo real.
                    </div>
                  </div>
                </div>
              )}

              {/* ─── CAPÍTULO 2 ────────────────────────────────────────────── */}
              {activeSection === 'owner' && (
                <div className="space-y-8">
                  <div className="border-b border-slate-100 pb-6">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Capítulo 2</span>
                    <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 mt-1 tracking-tight">
                      Guía del Propietario en la Asamblea
                    </h1>
                    <p className="text-sm text-slate-500 mt-2">
                      Aprende a navegar las diferentes herramientas de participación en vivo durante el evento.
                    </p>
                  </div>

                  <p className="text-slate-600 leading-relaxed">
                    Al ingresar al sistema durante el transcurso de una asamblea convocada, accederás al <strong>Dashboard de Participación</strong>. La interfaz está optimizada para ser clara, fluida y totalmente responsiva en computadores, tabletas y teléfonos móviles.
                  </p>

                  <div className="space-y-6">
                    {/* Pestaña de Agenda */}
                    <div className="rounded-2xl border border-slate-200 p-6 bg-white shadow-sm hover:border-slate-300 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                          <CalendarIcon className="w-5 h-5 stroke-[2]" />
                        </div>
                        <h3 className="text-base font-bold text-slate-900">Pestaña de Agenda (Orden del Día)</h3>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Aquí podrás consultar en cualquier momento la lista oficial de puntos programados para la asamblea. El sistema cuenta con indicadores de estado visuales que te permitirán saber con exactitud:
                      </p>
                      <ul className="list-disc list-inside space-y-1.5 text-sm text-slate-600 mt-3 font-medium">
                        <li><span className="text-slate-400">Puntos Completados:</span> Temas que ya fueron presentados, debatidos y cerrados.</li>
                        <li><strong className="text-blue-600">Punto Activo en Discusión:</strong> El tema exacto que la asamblea está tratando en ese instante, sincronizado en vivo por el moderador.</li>
                        <li><span className="text-slate-500">Puntos Pendientes:</span> Los siguientes temas en el cronograma oficial.</li>
                      </ul>
                    </div>

                    {/* Pestaña de Votación */}
                    <div className="rounded-2xl border border-slate-200 p-6 bg-white shadow-sm hover:border-slate-300 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                          <ChartBarIcon className="w-5 h-5 stroke-[2]" />
                        </div>
                        <h3 className="text-base font-bold text-slate-900">Pestaña de Votaciones Electrónicas</h3>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Cuando la administración o el presidente de la asamblea habiliten una moción de votación, la pregunta y sus opciones (por ejemplo: <em>Aprobar estados financieros</em>, <em>Elección de revisor fiscal</em>, etc.) aparecerán instantáneamente en tu pantalla de forma automática, sin necesidad de recargar la página.
                      </p>
                      <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-600 space-y-1">
                        <p className="font-bold text-slate-700">Ponderación Legal Automática:</p>
                        <p>Al seleccionar una opción y confirmar tu voto, el sistema le asigna matemáticamente el valor exacto de tu coeficiente de propiedad horizontal registrado. Tu selección queda cifrada y registrada de forma inmutable para la compilación del acta.</p>
                      </div>
                    </div>

                    {/* Pestaña de Quórum */}
                    <div className="rounded-2xl border border-slate-200 p-6 bg-white shadow-sm hover:border-slate-300 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                          <UsersIcon className="w-5 h-5 stroke-[2]" />
                        </div>
                        <h3 className="text-base font-bold text-slate-900">Pestaña de Auditoría de Quórum</h3>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Muestra gráficos en tiempo real con el porcentaje de asistencia verificado en la sala virtual. Permite constatar si la asamblea cuenta con el quórum reglamentario (típicamente la mitad más uno de los coeficientes totales para decisiones ordinarias, o mayorías calificadas del 70% para reformas estatutarias o adiciones).
                      </p>
                    </div>

                    {/* Pestaña de Chat */}
                    <div className="rounded-2xl border border-slate-200 p-6 bg-white shadow-sm hover:border-slate-300 transition-all">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                          <ChatBubbleLeftRightIcon className="w-5 h-5 stroke-[2]" />
                        </div>
                        <h3 className="text-base font-bold text-slate-900">Pestaña de Chat Oficial</h3>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Espacio de comunicación bidireccional donde los participantes pueden solicitar la palabra de manera ordenada, hacer preguntas al consejo de administración o dejar constancias formales. Cada mensaje enviado cuenta con una marca de tiempo precisa y queda respaldado en el historial de la asamblea.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-6">
                    <h4 className="text-sm font-bold text-slate-900 mb-2">Indicador de Conexión en Vivo</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      En la esquina superior derecha verás un indicador visual. Un punto verde confirma que tu conexión por WebSockets está activa y recibiendo datos en tiempo real. Si experimentas interrupciones en tu red de internet, el sistema intentará reconectarse automáticamente de forma transparente.
                    </p>
                  </div>
                </div>
              )}

              {/* ─── CAPÍTULO 3 ────────────────────────────────────────────── */}
              {activeSection === 'delegation' && (
                <div className="space-y-8">
                  <div className="border-b border-slate-100 pb-6">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Capítulo 3</span>
                    <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 mt-1 tracking-tight">
                      Poderes y Delegación de Voto
                    </h1>
                    <p className="text-sm text-slate-500 mt-2">
                      Procedimiento formal para la representación de copropietarios ausentes con total validez legal.
                    </p>
                  </div>

                  <p className="text-slate-600 leading-relaxed">
                    La legislación colombiana permite que un copropietario que no pueda asistir a la asamblea delegue su derecho a voto y representación mediante un poder escrito. Para optimizar este proceso y evitar fraudes o suplantaciones, <strong>DH Assembly</strong> digitaliza y audita todo el flujo de delegación.
                  </p>

                  {/* Regla estricta */}
                  <div className="rounded-2xl border-2 border-amber-500/20 bg-amber-50/50 p-6 space-y-3">
                    <div className="flex items-center gap-2 text-amber-900 font-bold">
                      <ShieldCheckIcon className="w-5 h-5 text-amber-600 shrink-0" />
                      <span>Regla de Privacidad y Representación Cerrada</span>
                    </div>
                    <p className="text-sm text-amber-900/90 leading-relaxed font-medium">
                      Por motivos estrictos de seguridad y confidencialidad comunitaria, en la plataforma <strong>no existen roles de delegados externos o invitados ajenos al conjunto</strong>. Un propietario <strong className="underline">únicamente</strong> puede ceder su poder de representación a otro propietario registrado en el padrón oficial del mismo conjunto residencial.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-slate-900">Paso a paso para delegar tu voto:</h3>
                    <ol className="list-decimal list-inside space-y-4 text-sm text-slate-600">
                      <li className="leading-relaxed">
                        <strong className="text-slate-900">Inicia la solicitud:</strong> En tu panel de asambleas programadas, ubica el evento próximo y haz clic en el botón <strong>"Gestionar delegación"</strong>.
                      </li>
                      <li className="leading-relaxed">
                        <strong className="text-slate-900">Selecciona al apoderado:</strong> Utiliza el buscador interno para ingresar el nombre o número de apartamento del vecino a quien deseas otorgar el poder. El sistema autocompletará la información basada en los registros validados.
                      </li>
                      <li className="leading-relaxed">
                        <strong className="text-slate-900">Notificación automática:</strong> Al confirmar, el sistema enviará de inmediato una notificación por correo electrónico al vecino seleccionado, informándole de tu solicitud.
                      </li>
                      <li className="leading-relaxed">
                        <strong className="text-slate-900">Aceptación y respaldo PDF:</strong> El vecino apoderado deberá hacer clic en el enlace de aceptación seguro. En ese instante, la plataforma genera automáticamente un <strong>Poder Notarial Digital en formato PDF</strong> con un código único de verificación, el cual queda archivado en los registros del evento.
                      </li>
                      <li className="leading-relaxed">
                        <strong className="text-slate-900">Efecto en la asamblea:</strong> Durante la sesión en vivo, el coeficiente de copropiedad del apoderado sumará automáticamente tu coeficiente delegado. Al emitir un voto en cualquier moción, este aplicará con el peso consolidado de ambas partes.
                      </li>
                    </ol>
                  </div>

                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500 space-y-1">
                    <p className="font-bold text-slate-700">Cancelación de Poderes:</p>
                    <p>Si el propietario original decide finalmente asistir a la asamblea e ingresa a la plataforma con su usuario antes de iniciar la sesión, el sistema anulará de forma automática la delegación otorgada previamente, devolviéndole su control de voto individual en cumplimiento de la prelación legal.</p>
                  </div>
                </div>
              )}

              {/* ─── CAPÍTULO 4 ────────────────────────────────────────────── */}
              {activeSection === 'admin' && (
                <div className="space-y-8">
                  <div className="border-b border-slate-100 pb-6">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Capítulo 4</span>
                    <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 mt-1 tracking-tight">
                      Panel de Administración Avanzado
                    </h1>
                    <p className="text-sm text-slate-500 mt-2">
                      Guía técnica y operativa para administradores, gerentes y presidentes de asamblea.
                    </p>
                  </div>

                  <p className="text-slate-600 leading-relaxed">
                    El rol de <strong>Administrador</strong> provee acceso irrestricto a la consola de control. Desde allí se gestiona el ciclo de vida completo del evento, garantizando un flujo ordenado y respaldado técnicamente.
                  </p>

                  <div className="space-y-6">
                    {/* Importación */}
                    <div className="border-l-4 border-blue-600 pl-5 py-1 space-y-2">
                      <h3 className="text-base font-bold text-slate-900">1. Padrón Electoral e Importación Masiva</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Para registrar a la comunidad, el administrador debe preparar un archivo de Excel (<code>.xlsx</code> o <code>.xls</code>) con la estructura de columnas estandarizada: <strong>Torre</strong>, <strong>Apartamento</strong>, <strong>Nombre Completo</strong>, <strong>Correo Electrónico</strong>, <strong>Identificación</strong> y <strong>Coeficiente</strong>.
                      </p>
                      <p className="text-xs text-slate-500">
                        El motor de importación procesa los registros de forma asíncrona, validando que la sumatoria de coeficientes no exceda el 100% y notificando de inmediato mediante correos de bienvenida (vía servicio Resend) a cada titular importado.
                      </p>
                    </div>

                    {/* Gestión de quórum */}
                    <div className="border-l-4 border-blue-600 pl-5 py-1 space-y-2">
                      <h3 className="text-base font-bold text-slate-900">2. Control y Verificación de Quórum</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Antes de dar inicio oficial al orden del día, el panel muestra la sumatoria exacta del quórum conectado. El sistema diferencia claramente entre:
                      </p>
                      <ul className="list-disc list-inside text-xs text-slate-600 space-y-1 font-medium">
                        <li>Coeficiente presente directamente en sala.</li>
                        <li>Coeficiente representado legalmente a través de poderes validados.</li>
                      </ul>
                      <p className="text-xs text-slate-500">
                        Una vez alcanzado el umbral legal requerido para el tipo de asamblea, el administrador puede presionar el botón de <strong>"Iniciar Asamblea"</strong>, lo que habilita la interactividad para los copropietarios.
                      </p>
                    </div>

                    {/* Votaciones */}
                    <div className="border-l-4 border-blue-600 pl-5 py-1 space-y-2">
                      <h3 className="text-base font-bold text-slate-900">3. Creación y Moderación de Votaciones</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        El administrador puede redactar mociones en tiempo real o precargarlas durante la planeación. Al seleccionar una pregunta y marcarla como "Activa", esta se transmite inmediatamente a las pantallas de toda la audiencia por WebSockets.
                      </p>
                      <p className="text-xs text-slate-500">
                        El panel permite monitorear una barra de progreso en vivo con la cantidad de votos emitidos. Al presionar <strong>"Cerrar Votación"</strong>, los resultados se tabulan instantáneamente con la sumatoria de coeficientes, proyectando los gráficos definitivos y declarando si la moción fue aprobada o rechazada.
                      </p>
                    </div>

                    {/* Actas */}
                    <div className="border-l-4 border-blue-600 pl-5 py-1 space-y-2">
                      <h3 className="text-base font-bold text-slate-900">4. Cierre Técnico y Compilación de Actas PDF</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        Al concluir todos los puntos del orden del día, se ejecuta la acción de <strong>"Completar Asamblea"</strong>. En ese momento, la plataforma activa un flujo de trabajo en background impulsado por colas de alta fidelidad (Bull y Redis) que compila un documento PDF exhaustivo.
                      </p>
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-600 space-y-1">
                        <p className="font-bold text-slate-700">El acta generada automáticamente incluye:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          <li>Encabezado formal con fecha, hora de inicio y finalización, y modalidad del evento.</li>
                          <li>Listado completo y auditado del quórum inicial y final verificado.</li>
                          <li>Registro detallado de todas las votaciones realizadas, con el desglose exacto de aprobación por coeficiente.</li>
                          <li>Detalle de los poderes ejercidos durante la asamblea.</li>
                          <li>Bloques de firma digital incrustados para el presidente y secretario de la asamblea.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Basic Footer */}
      <footer className="border-t border-slate-200 mt-20 py-8 text-center text-xs text-slate-400 bg-white">
        <p>&copy; 2019-{currentYear} <strong>DHING ®</strong>. Manual Oficial y Técnico de DH Assembly.</p>
      </footer>
    </div>
  );
};
export default ManualPage;
